import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, Button, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { toast } from "react-toastify";
import Loader from "../../../components/partials/components/loader";
import RenderDeleteModal from "../../../components/partials/components/delete-modal";
import { EditIcon, DeleteIcon } from "../../../components/setting/elements";
import { getApiCall, postApiCall } from "../../../service/httpCall";
import {
  API_DISPOSITION_DELETE,
  API_DISPOSITION_LIST,
} from "../../../service/url";
import exportData from "../../../utilities/export";
import { getUser } from "../../../utilities/setting";
import { MODULE_ACL, ROUTES, USER_ROLES } from "../../../utilities/constant";
import { exportToCSV, hasPermission } from '../../../utilities/helpers';

const EXPORT_MODEL = "DispositionForm";

const getCreatedByLabel = (rowData) => {
  const username = rowData?.created_by_user?.username;
  if (username) return username;
  const id = rowData?.created_by_user?.id;
  return id ? String(id) : "—";
};

const DispositionFormsList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dataList, setDataList] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [searchName, setSearchName] = useState("");
  const [searchCreatedBy, setSearchCreatedBy] = useState("");

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [buttonDisable, setButtonDisable] = useState(false);
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState({});

  const fetchData = async () => {
    return getApiCall(API_DISPOSITION_LIST)
      .then((resp) => {
        const updated = Array.isArray(resp)
          ? resp.map((val, idx) => ({ ...val, key: idx + 1 }))
          : [];
        setDataList(updated);
        setOriginalData(updated);
      })
      .catch((err) => toast.error(err || "Unable to load disposition forms"));
  };

  useEffect(() => {
    setLoading(true);
    const user = getUser();
      if (!user) return;
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
      if (user?.acl_settings) {
        setLoggedInUserACL(JSON.parse(user.acl_settings.module_operations));
      }
    fetchData().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nameQuery = searchName.trim().toLowerCase();
    const createdByQuery = searchCreatedBy.trim().toLowerCase();

    if (!nameQuery && !createdByQuery) {
      setDataList(originalData);
      return;
    }

    const filtered = originalData.filter((item) => {
      const matchesName = nameQuery
        ? String(item?.name || "")
            .toLowerCase()
            .includes(nameQuery)
        : true;

      const matchesCreatedBy = createdByQuery
        ? getCreatedByLabel(item).toLowerCase().includes(createdByQuery)
        : true;

      return matchesName && matchesCreatedBy;
    });

    setDataList(filtered);
  }, [searchName, searchCreatedBy, originalData]);

  useEffect(() => {
    if (!confirmDelete || !deleteId) return;

    postApiCall(API_DISPOSITION_DELETE, { dispositionFormIds: [deleteId] })
      .then(() => {
        toast.success("Disposition form deleted successfully");
        fetchData();
      })
      .catch((err) => toast.error(err || "Unable to delete disposition form"))
      .finally(() => {
        setConfirmDelete(false);
        setEnableDeleteModal(false);
        setDeleteId(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmDelete]);

  const canAdd =
    isSuperAdmin || loggedInUserACL?.[MODULE_ACL.CALL_GROUP]?.add;
  const canEdit =
    isSuperAdmin || loggedInUserACL?.[MODULE_ACL.CALL_GROUP]?.edit;
  const canDelete =
    isSuperAdmin || loggedInUserACL?.[MODULE_ACL.CALL_GROUP]?.delete;

  const getFieldsArray = (rowData) => {
    if (Array.isArray(rowData?.fields)) return rowData.fields;
    if (typeof rowData?.fields === "string") {
      try {
        const parsed = JSON.parse(rowData.fields);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const actionTemplate = (rowData) => (
    <div>
      <div className="d-flex align-items-center list-user-action">
        <button
          type="button"
          className="btn btn-sm btn-icon btn-info"
          title="View"
          style={{ marginRight: "10px" }}
          onClick={() => {
            setViewRow(rowData);
            setShowViewModal(true);
          }}
        >
          <span className="btn-inner">
            <i className="fa fa-eye" aria-hidden="true" />
          </span>
        </button>
        {canEdit && (
          <EditIcon
            linkTo={`/${ROUTES.DISPOSITION_FORMS.EDIT.replace(":id", rowData.id)}`}
          />
        )}
        <div style={{ marginLeft: "10px" }}>
          {canDelete && (
            <DeleteIcon
              handleCallback={() => {
                setDeleteId(rowData.id);
                setEnableDeleteModal(true);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );

  const fieldsCountTemplate = (rowData) => (
    <span>{Array.isArray(rowData?.fields) ? rowData.fields.length : 0}</span>
  );

  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

    useEffect(() => {
      const user = getUser();
        if (user) {
          if (user?.acl_settings) {
            setModuleOperationsForReadPermissionCheck(JSON.parse(user.acl_settings.module_operations));
          }
        }
    }, []);

    useEffect(() => {
      const onAclRefreshed = () => {
        const user = getUser();
        if (user?.acl_settings?.module_operations) {
          try {
            const parsed = typeof user.acl_settings.module_operations === "string"
              ? JSON.parse(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
            setLoggedInUserACL(parsed);
            setModuleOperationsForReadPermissionCheck(parsed);
          } catch (e) {
            console.log("Failed to apply refreshed ACL", e);
          }
        }
      };
      window.addEventListener("acl-refreshed", onAclRefreshed);
      return () => window.removeEventListener("acl-refreshed", onAclRefreshed);
    }, []);

  const dateTemplate = (rowData, field) => {
    const value = rowData?.[field];
    if (!value) return "—";
    return new Date(value).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const callFlowTemplate = (rowData) => rowData?.call_direction.toUpperCase();

  const createdByBody = (rowData) => <span>{getCreatedByLabel(rowData)}</span>;

  const canShowCreatedByFilter = useMemo(() => isSuperAdmin, [isSuperAdmin]);

  const handleExport = async () => {
   try {
      setButtonDisable(true);

      exportToCSV({
         fileName: "disposition-form-list",
         data: dataList,
         columns: [
            {
               header: "Name",
               accessor: (row) =>
                  row.name || "-"
            },

            ...(isSuperAdmin
               ? [{
                  header: "Created By",
                  accessor: (row) =>
                     row.creator?.username ||
                     row.created_by?.username ||
                     "-"
               }]
               : []),

            {
               header: "Fields",
               accessor: (row) =>
                  row.fields?.length || 0
            },
            {
               header: "Created Date",
               accessor: (row) =>
                  dateTemplate(row, "created_at")
            },
            {
               header: "Updated Date",
               accessor: (row) =>
                  dateTemplate(row, "updated_at")
            }
         ]
      });

   } finally {
      setButtonDisable(false);
   }
};

const canRead = hasPermission(
     moduleOperationsForReadPermissionCheck,
     "Disposition Forms",
     "read",
   );
 
   if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
     return (
       <div
         className="text-center p-4"
         style={{
           marginTop: "50px",
           marginBottom: "50px",
           fontSize: "16px",
         }}
       >
         No read permission found in assigned ACL
       </div>
     );
   }

  if (loading) return <Loader />;

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <h4 className="card-title">Disposition Forms</h4>
              <div className="d-flex gap-2">
                {dataList?.length > 0 && (
                  <Button
                    variant="secondary"
                    disabled={buttonDisable}
                    onClick={handleExport}
                  >
                    Export
                  </Button>
                )}
                {canAdd && (
                  <Button
                    onClick={() =>
                      navigate(`/${ROUTES.DISPOSITION_FORMS.CREATE}`)
                    }
                  >
                    Create
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <Form.Control
                  className="w-25"
                  placeholder="Search by name..."
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                />
                {canShowCreatedByFilter && (
                  <Form.Control
                    className="w-25"
                    placeholder="Search by created by..."
                    value={searchCreatedBy}
                    onChange={(e) => setSearchCreatedBy(e.target.value)}
                  />
                )}
              </div>

              <DataTable
                value={dataList}
                paginator
                rows={10}
                style={{ width: "100%" }}
              >
                <Column field="name" header="Name" />
                <Column header="Call flow" body={callFlowTemplate} />
                {isSuperAdmin && (
                  <Column header="Created By" body={createdByBody} />
                )}
                <Column header="Fields" body={fieldsCountTemplate} />
                <Column
                  header="Created Date"
                  body={(rowData) => dateTemplate(rowData, "created_at")}
                />
                <Column
                  header="Updated Date"
                  body={(rowData) => dateTemplate(rowData, "updated_at")}
                />
                <Column header="Action" body={actionTemplate} />
              </DataTable>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setViewRow(null);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Disposition Form Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="mb-3">
              <div>
                <strong>Name:</strong> {viewRow?.name || "—"}
              </div>
              <div>
                <strong>Call flow:</strong> {callFlowTemplate(viewRow)}
              </div>
              {isSuperAdmin && (
              <div>
                <strong>Created By:</strong> {getCreatedByLabel(viewRow)}
              </div>
            )}
          </div>

          <div className="table-responsive">
            <table className="table table-sm table-bordered">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Default</th>
                  <th>Min</th>
                  <th>Max</th>
                </tr>
              </thead>
              <tbody>
                {getFieldsArray(viewRow).length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center">
                      No fields found
                    </td>
                  </tr>
                ) : (
                  getFieldsArray(viewRow).map((f, idx) => (
                    <tr key={idx}>
                      <td>{f?.field_name ?? "—"}</td>
                      <td>{f?.field_type ?? "—"}</td>
                      <td>{f?.is_required ? "Yes" : "No"}</td>
                      <td>{(f?.field_default !== undefined && f?.field_default !== null) ? String(f?.field_default) : "—"}</td>
                      <td>{f?.field_min ?? "—"}</td>
                      <td>{f?.field_max ?? "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowViewModal(false);
              setViewRow(null);
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <RenderDeleteModal
        show={enableDeleteModal}
        setShow={setEnableDeleteModal}
        setConfirmDelete={setConfirmDelete}
      />
    </>
  );
};

export default DispositionFormsList;
